<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import { addToast } from '../../lib/toast'
  import { api } from '../../lib/api'
  import { slide } from 'svelte/transition'

  export let sessionId: string
  export let contacts: any[] = []
  export let selectedContact: any = null

  const dispatch = createEventDispatcher()

  let to = ''
  let messageText = ''
  let filePath = ''
  let fileCaption = ''
  let activeTab: 'text' | 'file' = 'text'
  let sending = false

  // Contact select state
  let isContactSelectOpen = false
  let contactSearch = ''
  let contactSelectEl: HTMLElement

  $: filteredContacts = contacts.filter(c =>
    (c.identifier && c.identifier.toLowerCase().includes(contactSearch.toLowerCase())) ||
    c.phone_number.includes(contactSearch)
  )

  function toggleContactSelect() {
    isContactSelectOpen = !isContactSelectOpen
    if (isContactSelectOpen) {
      setTimeout(() => document.getElementById('contact-search')?.focus(), 100)
    } else {
      contactSearch = ''
    }
  }

  function selectContact(contact: any) {
    to = contact.phone_number
    dispatch('contactSelected', contact)
    isContactSelectOpen = false
    contactSearch = ''
  }

  function handleClickOutside(event: MouseEvent) {
    if (isContactSelectOpen && contactSelectEl && !contactSelectEl.contains(event.target as Node)) {
      isContactSelectOpen = false
      contactSearch = ''
    }
  }

  // Validación específica de WhatsApp
  function validateWhatsAppNumber(number: string): { valid: boolean; error?: string; normalized?: string } {
    if (!number || number.trim() === '') {
      return { valid: false, error: 'El número de teléfono es requerido' }
    }

    // Remover espacios y caracteres especiales excepto +
    let cleaned = number.replace(/\s+/g, '').replace(/[^\d+]/g, '')
    
    // Si tiene 9 dígitos, agregar prefijo 51
    if (/^\d{9}$/.test(cleaned)) {
      // Validar que empiece con 9
      if (!cleaned.startsWith('9')) {
        return { valid: false, error: 'El número debe empezar con 9 y tener 9 dígitos (ej: 987654321)' }
      }
      cleaned = '51' + cleaned
    } else if (cleaned.startsWith('51')) {
      // Si ya tiene el prefijo 51, validar que tenga 9 dígitos después
      const withoutPrefix = cleaned.substring(2)
      if (!/^\d{9}$/.test(withoutPrefix)) {
        return { valid: false, error: 'El número debe tener 9 dígitos después del prefijo 51 (ej: 51987654321)' }
      }
      // Validar que empiece con 9
      if (!withoutPrefix.startsWith('9')) {
        return { valid: false, error: 'El número debe empezar con 9 y tener 9 dígitos (ej: 51987654321)' }
      }
    } else {
      return { valid: false, error: 'Formato inválido. Ingresa 9 dígitos empezando con 9 (el prefijo 51 se agrega automáticamente) o el número completo con código de país' }
    }
    
    // Validar formato final: debe empezar con 51 seguido de 9 dígitos que empiecen con 9
    if (!/^51\d{9}$/.test(cleaned)) {
      return { valid: false, error: 'Formato inválido. Ingresa 9 dígitos empezando con 9 (el prefijo 51 se agrega automáticamente) o el número completo con código de país' }
    }

    // Validar que el número después del prefijo empiece con 9
    const numberPart = cleaned.substring(2)
    if (!numberPart.startsWith('9')) {
      return { valid: false, error: 'El número debe empezar con 9 y tener 9 dígitos (ej: 987654321)' }
    }

    return { valid: true, normalized: cleaned }
  }

  function validateMessage(message: string): { valid: boolean; error?: string } {
    if (!message || message.trim() === '') {
      return { valid: false, error: 'El mensaje es requerido' }
    }

    if (message.length > 4096) {
      return { valid: false, error: 'El mensaje no puede exceder 4096 caracteres' }
    }

    return { valid: true }
  }

  async function sendMessage() {
    // Validar destinatario
    const toValidation = validateWhatsAppNumber(to)
    if (!toValidation.valid) {
      addToast(toValidation.error || 'Número inválido', 'error')
      return
    }

    if (activeTab === 'text') {
      // Validar mensaje
      const msgValidation = validateMessage(messageText)
      if (!msgValidation.valid) {
        addToast(msgValidation.error || 'Mensaje inválido', 'error')
        return
      }

      sending = true
      try {
        await api.post('/api/v1/sendText', {
          sessionId: sessionId,
          to: toValidation.normalized || to,
          text: messageText
        })
        addToast('Mensaje enviado correctamente', 'success')
        messageText = ''
        to = ''
        dispatch('sent')
      } catch (e: any) {
        addToast(e.message || 'Error al enviar mensaje', 'error')
      } finally {
        sending = false
      }
    } else {
      // Validar archivo
      if (!filePath || filePath.trim() === '') {
        addToast('La ruta o URL del archivo es requerida', 'error')
        return
      }

      sending = true
      try {
        await api.post('/api/v1/sendImage', {
          sessionId: sessionId,
          to: toValidation.normalized || to,
          path: filePath,
          caption: fileCaption
        })
        addToast('Archivo enviado correctamente', 'success')
        filePath = ''
        fileCaption = ''
        to = ''
        dispatch('sent')
      } catch (e: any) {
        addToast(e.message || 'Error al enviar archivo', 'error')
      } finally {
        sending = false
      }
    }
  }

  // Sincronizar con contactos seleccionados externamente
  $: if (selectedContact) {
    to = selectedContact.phone_number
  }

  // Limpiar contacto cuando se cambia el número manualmente
  $: if (to && selectedContact && selectedContact.phone_number !== to) {
    dispatch('contactCleared')
  }
</script>

<svelte:window on:click={handleClickOutside} />

<div class="space-y-5">
  <!-- Identificador del formulario -->
  <div class="flex items-center gap-3 pb-4 border-b border-white/10">
    <div class="flex items-center justify-center w-10 h-10 rounded-xl bg-green-500/20 border border-green-500/30">
      <span class="text-2xl">📱</span>
    </div>
    <div>
      <h3 class="text-lg font-black text-white">Formulario de WhatsApp</h3>
      <p class="text-xs text-indigo-200/60">Envía mensajes y archivos a números de teléfono</p>
    </div>
  </div>

  <!-- Tabs -->
  <div class="flex gap-2 border-b border-white/10">
    <button
      on:click={() => activeTab = 'text'}
      class="px-6 py-3 font-bold text-sm transition-all relative {activeTab === 'text' ? 'text-pink-400' : 'text-indigo-200/60 hover:text-indigo-200'}"
    >
      💬 Mensaje de Texto
      {#if activeTab === 'text'}
        <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500"></div>
      {/if}
    </button>
    <button
      on:click={() => activeTab = 'file'}
      class="px-6 py-3 font-bold text-sm transition-all relative {activeTab === 'file' ? 'text-pink-400' : 'text-indigo-200/60 hover:text-indigo-200'}"
    >
      🖼️ Archivo
      {#if activeTab === 'file'}
        <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500"></div>
      {/if}
    </button>
  </div>

  <!-- Destinatario -->
  <div>
    <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
      Destinatario (Número de Teléfono)
      {#if selectedContact}
        <span class="ml-2 text-pink-400 font-normal normal-case">({selectedContact.identifier || selectedContact.phone_number})</span>
      {/if}
    </label>
    <div class="flex-1 relative" bind:this={contactSelectEl}>
      <input 
        bind:value={to}
        placeholder="Ej: 987654321"
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
      />
      {#if contacts.length > 0}
        <button
          on:click|stopPropagation={toggleContactSelect}
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-indigo-200/50 hover:text-pink-400 transition-colors"
        >
          <svg class="w-5 h-5 {isContactSelectOpen ? 'rotate-180' : ''}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      {/if}
      
      {#if isContactSelectOpen && contacts.length > 0}
        <div 
          transition:slide={{ duration: 200 }}
          class="absolute top-full left-0 right-0 mt-2 bg-[#1a1b26] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div class="p-3 border-b border-white/5">
            <input 
              id="contact-search"
              bind:value={contactSearch}
              placeholder="Buscar contacto..."
              class="w-full bg-black/20 border border-white/5 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-pink-500/50 placeholder-white/20"
              on:click|stopPropagation
            />
          </div>
          <div class="max-h-60 overflow-y-auto custom-scrollbar">
            {#if filteredContacts.length === 0}
              <div class="p-4 text-center text-indigo-200/40 text-sm">No se encontraron contactos</div>
            {:else}
              {#each filteredContacts as contact}
                <button 
                  on:click|stopPropagation={() => selectContact(contact)}
                  class="w-full text-left px-5 py-3 hover:bg-white/5 transition-colors flex items-center justify-between group"
                >
                  <div>
                    <div class="font-medium text-white group-hover:text-pink-300 transition-colors">
                      {contact.identifier || contact.phone_number}
                    </div>
                    {#if contact.identifier && contact.identifier !== contact.phone_number}
                      <div class="text-xs text-indigo-200/40">{contact.phone_number}</div>
                    {/if}
                  </div>
                </button>
              {/each}
            {/if}
          </div>
        </div>
      {/if}
    </div>
    <p class="text-sm text-indigo-300/80 mt-2 ml-1 font-medium">
      Ingresa 9 dígitos empezando con 9 (ej: 987654321). El prefijo 51 se agrega automáticamente.
    </p>
  </div>

  <!-- Contenido según tab -->
  {#if activeTab === 'text'}
    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Mensaje</label>
      <textarea 
        bind:value={messageText}
        rows="4"
        placeholder="Escribe tu mensaje aquí..."
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 resize-none"
      ></textarea>
      <p class="text-xs text-indigo-300/60 mt-1 ml-1">Máximo 4096 caracteres</p>
    </div>
  {:else}
    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Ruta o URL del archivo</label>
      <div class="relative group">
        <input 
          bind:value={filePath}
          placeholder="https://... o /ruta/local/..."
          class="w-full px-4 py-3 pl-10 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
        />
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-indigo-200/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
      </div>
      <p class="text-sm text-indigo-300/80 mt-2 ml-1 font-medium">
        Solo se permiten: Imágenes y Documentos (JPG, PNG, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, etc.)
      </p>
      <p class="text-xs text-yellow-300/70 mt-1 ml-1 font-medium">
        ⚠️ No se permiten: Audio ni Video • Tamaño máximo: 10MB
      </p>
    </div>

    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Caption (Opcional)</label>
      <input 
        bind:value={fileCaption}
        placeholder="Descripción del archivo..."
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
      />
    </div>
  {/if}

  <!-- Botón Enviar -->
  <button
    on:click={sendMessage}
    disabled={sending || !to}
    class="w-full px-6 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
  >
    {sending ? 'Enviando...' : '📤 Enviar Mensaje'}
  </button>
</div>

<style>
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
  }
</style>

